import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useLocation } from "react-router";
import queryString, { ParsedQuery } from "query-string";
import { LeadColumns as columns} from "./leads-table-header";
import AppLayout from "../../core/layouts/AppLayout";
import "../../styles/listing.scss";
import "../../styles/tags.scss";
import "../../styles/import-lead.scss";

import Pagination from "../../core/components/pagination/Pagination";
import Moment from "react-moment";
import moment from "moment";
import classnames from "classnames";
import {
  ListTable,
  ListHeader,
  ListHeaderSearch,
  ListHeaderDropdown,
  Dict,
  FilterValueType,
  ListFilters,
  getFilters,
  PaginatedApiResponse,
  Lead,
  SavedFilter,
  ListColumnSettings,
  Column,
  ListEntityType,
  ListSort,
  SortOrder,
  AppPermissions,
  useHasPermission,
  QueryFilter,
  convertPascalCaseToWords,
  LeadStage,
  SingleSelectFilter,
  City,
  User,
} from "../../core";
import MultiSelectFilter from "../../core/components/list-filters/MultiSelectFilter";
import { useMasterData } from "../../core/hooks/useMasterData";
import api from "../../api";
import { useDataLoader, usePerformAction } from "react-remote-data-hooks";
import { ContainerLoader, Spinner } from "../../components/loader";
import AddLeadForm from "./AddLeadForm";
import { BulkAction } from "../bulk-action/bulk-action";
import { toastr } from "react-redux-toastr";
import { IListTableColumnProps } from "../../core/components/list-table/ListTableColumn";
import { LeadCalendarProvider } from "../../core/hooks/useLeadCalendarContext";
import { RouteKeys } from "../routes/route-keys";
import DateRangeListFilter, {
  IDateRange,
} from "../../core/components/list-filters/DateRangeListFilter";
import StaffMultiSelectFilter from "../../core/components/list-filters/StaffMultiSelectFilter";
import { ConfirmationDialogProvider } from "../../core/components/confirm-popup/confirm-popup";

const Home: React.FunctionComponent = () => {
  const canViewLeadDetails = useHasPermission(AppPermissions.ViewLeadDetails);
  const canCreateLeads = useHasPermission(AppPermissions.CreateLeads);
  const canBulkImportLead = useHasPermission(AppPermissions.BulkImportLead);
  const canDownloadLeads = useHasPermission(AppPermissions.DownloadLeads);
  const canBulkAssignLeads = useHasPermission(AppPermissions.BulkAssignLeads);
  const canDeleteLeads = useHasPermission(AppPermissions.DeleteLeads);
  const canViewArchivedLeads = useHasPermission(AppPermissions.viewArchivedLeads);
  const history = useHistory();
  const params = useLocation().search;
  const masterData = useMasterData(api.settings);
  const [search, setSearch] = useState("");
  const [showBulkActionPopup, setShowBulkActionPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [leadModal, setLeadModal] = useState<{
    isOpen?: boolean;
    leadId?: string;
  }>({});

  const { data: leadSettings, reload: reloadSettings } = useDataLoader<{
    filters: SavedFilter[];
    columnSettings?: ListColumnSettings;
  }>(() => api.settings.getListColumnSettings(ListEntityType.Lead));

  const {
    performAction: saveFilter,
    isPerforming: savingFilter,
  } = usePerformAction(
    (fls: Dict<FilterValueType>, name: string) =>
      api.settings.saveFilter(fls, name, ListEntityType.Lead),
    () => {
      toastr.success("Success", "Filter Saved");
      reloadSettings();
    },
    () => {
      toastr.error("Error", "Unable to save filter");
    }
  );

  const { performAction: removeFilter } = usePerformAction(
    (_id: string) => api.settings.removeSavedFilter(_id),
    () => {
      toastr.success("Success", "Filter Removed");
      reloadSettings();
    },
    () => {
      toastr.error("Error", "Unable to remove filter");
    }
  );

  const { performAction: saveColumnSettings } = usePerformAction(
    (cols: Column[]) =>
      api.settings.saveListColumnSettings(cols, ListEntityType.Lead),
    reloadSettings
  );

  const loadFromQuery = () => {
    const queryFilter: QueryFilter = {};
    const queries: ParsedQuery<string> = queryString.parse(params);
    queryFilter.query = queries.query as string;
    if (queries.filters) {
      queryFilter.filters = JSON.parse(queries.filters?.toString());
    }
    if (queries.page) {
      queryFilter.page = Number(queries.page || 1);
    }
    if (queries.perPage) {
      queryFilter.perPage = Number(queries.perPage || 1);
    }
    if (queries.startDate) {
      queryFilter.startDate = moment(queries.startDate as string).toDate();
    }
    if (queries.endDate) {
      queryFilter.endDate = moment(queries.endDate as string).toDate();
    }

    if (queries.sort) {
      queryFilter.sort = JSON.parse(queries.sort?.toString());
    }

    return queryFilter;
  };

  const [filters, setFilters] = useState<Dict<FilterValueType>>(
    loadFromQuery()?.filters || {}
  );

  const [sort, setSort] = useState<ListSort>({
    sortBy: loadFromQuery()?.sort?.sortBy || "createdAt",
    sortOrder: loadFromQuery()?.sort?.sortOrder || SortOrder.DESC,
  });

  const [dateFilters, setDateFilters] = useState<IDateRange>({
    startDate: loadFromQuery()?.startDate,
    endDate: loadFromQuery()?.endDate,
  });

  const years = new Set(
    masterData.data?.vehicleModels?.map((vehicleModel) =>
      Number(vehicleModel.year)
    )
  );
  const yearsList = [...years].sort().reverse();
  const yearFilterOptions: { year: number }[] = [];
  yearsList.map((year) => yearFilterOptions.push({ year }));

  const [page, setPage] = useState(loadFromQuery()?.page || 1);
  const [perPage, setPerPage] = useState(loadFromQuery()?.perPage || 20);
  const [showArchived, setShowArchived] = useState(false);

  const { data, loading, loaded, reload } = useDataLoader<
    PaginatedApiResponse<Lead> & { lastUpdatedAt: Date }
  >(
    (
      searchTerm: string | undefined,
      searchFilters: Dict<FilterValueType>,
      currentPage: number,
      pageCount: number,
      searchOrder?: ListSort,
      dates?: IDateRange,
      showArchvd?: boolean
    ) =>
      api.lead.getLeads({
        fromDate: dates?.startDate
          ? moment(dates?.startDate).startOf("day").toDate()
          : undefined,
        toDate: dates?.endDate
          ? moment(dates?.endDate).endOf("day").toDate()
          : undefined,
        ...getFilters(loadFromQuery()?.filters || searchFilters),
        page: currentPage,
        query: loadFromQuery()?.query || searchTerm,
        sortBy: searchOrder?.sortBy,
        sortOrder: searchOrder?.sortOrder,
        perPage: pageCount,
        isDeal: false,
        isArchived: showArchvd,
      }),
    [search, filters, page, perPage, sort, dateFilters]
  );

  const { data: allStaffs } = useDataLoader<User[]>(() =>
    api.user.getAllStaffs({
      sortBy: "name",
      sortOrder: SortOrder.ASC,
    })
  );

  useEffect(() => {
    if (loaded) {
      setPage(1);
      onChangeFilter(filters, search, 1, perPage, dateFilters, sort);
      reload(search, filters, 1, perPage, sort, dateFilters, showArchived);
    }
  }, [search, filters, sort, perPage, dateFilters]);

  useEffect(() => {
    if (loaded) {
      reload(search, filters, page, perPage, sort, dateFilters, showArchived);
    }
  }, [page]);

  useEffect(() => {
    if (loaded) {
      setPage(1);
      reload(search, filters, 1, perPage, sort, dateFilters, showArchived);
    }
  }, [showArchived]);

  useEffect(() => {
    const filterResonse = loadFromQuery();

    if (filterResonse.query) {
      setSearch(filterResonse.query as string);
    }
    if (filterResonse.filters) {
      setFilters(filterResonse.filters);
    }
    if (filterResonse.page) {
      setPage(filterResonse.page);
    }
    if (filterResonse.perPage) {
      setPerPage(filterResonse.perPage);
    }

    if (filterResonse.startDate && filterResonse.endDate) {
      setDateFilters({
        startDate: filterResonse.startDate,
        endDate: filterResonse.endDate,
      });
    }
  }, []);

  const [selected, setSelected] = useState<string[]>([]);

  const onSaveFilter = (name: string) => {
    saveFilter(filters, name);
  };

  const onRemoveFilter = (_id: string) => {
    removeFilter(_id);
  };

  const onRemoveLeads = async () => {
    try {
      await api.lead.bulkDelete({ leadIds: selected });
      toastr.success("Success", "Leads deleted successfully");
      setShowDeletePopup(false);
      reload(search, filters, 1, perPage, sort, dateFilters, showArchived);
    } catch (error) {
      toastr.error("Error", error?.response?.data?.message);
    }
  };

  const leadCreated = () => {
    setPage(1);
    onChangeFilter(filters, search, 1, perPage, dateFilters, sort);
    reload(search, filters, 1, perPage, sort, dateFilters, showArchived);
  };

  const onLeadClick = (id?: string) => {
    if (canViewLeadDetails) {
      history.push(`${RouteKeys.Leads}/${id || ""}`);
    }
  };

  const onChangeFilter = (
    searchFilters: Dict<FilterValueType>,
    searchValue: string,
    currentPage: number,
    numberOfPages: number,
    dates?: IDateRange,
    sortOption?: ListSort
  ) => {
    let query = `query=${encodeURIComponent(
      searchValue
    )}&page=${currentPage}&perPage=${numberOfPages}`;

    if (
      getFilters(searchFilters) &&
      Object.keys(getFilters(searchFilters)).length
    ) {
      query =
        query + `&filters=` + encodeURIComponent(JSON.stringify(searchFilters));
    }
    if (dates?.startDate) {
      query =
        query + `&startDate=` + moment(dates?.startDate).format("DD-MMM-YYYY");
    }
    if (dates?.endDate) {
      query =
        query + `&endDate=` + moment(dates?.endDate).format("DD-MMM-YYYY");
    }
    if (sortOption && sortOption.sortBy) {
      query = query + `&sort=` + JSON.stringify(sortOption);
    }

    history.replace(`/leads?${query}`);
  };

  const { data: allCities } = useDataLoader<City[]>(() =>
    api.AddNewRequirement.loadAllCities()
  );

  const {
    performAction: downloadLeads,
    isPerforming: downloading,
  } = usePerformAction(
    async () => {
      let excelColumns: {
        label: string;
        source?: string;
      }[] = leadSettings.columnSettings?.columns
        ?.map((column) => {
          const selectedColumn = columns.find(
            (col) => col.props.column === column.key
          ) as { props: IListTableColumnProps };
          if (selectedColumn && !selectedColumn.props.ignoreOnExcel) {
            return {
              label: selectedColumn.props.label || "",
              source:
                selectedColumn.props.excelSource ||
                selectedColumn.props.source?.toString(),
            };
          }
        })
        .filter((a) => !!a) as {
        label: string;
        source?: string;
      }[];
      if (!excelColumns?.length) {
        const allColumns = columns as { props: IListTableColumnProps }[];
        excelColumns = allColumns
          .filter((col) => col.props.showDefault && !col.props.ignoreOnExcel)
          .map((selectedColumn) => {
            return {
              label: selectedColumn.props.label || "",
              source:
                selectedColumn.props.excelSource ||
                selectedColumn.props.source?.toString(),
            };
          });
      }
      await api.lead.downloadLeads({
        fromDate: dateFilters?.startDate
          ? moment(dateFilters?.startDate).startOf("day").toDate()
          : undefined,
        toDate: dateFilters?.endDate
          ? moment(dateFilters?.endDate).endOf("day").toDate()
          : undefined,
        ...getFilters(loadFromQuery()?.filters || filters),
        query: loadFromQuery()?.query || search,
        sortBy: sort?.sortBy,
        sortOrder: sort?.sortOrder,
        columns: excelColumns.filter((a) => !!a.source),
        isDeal: false,
      });
    },
    () => {
      toastr.success("Success", "Downloaded successfully");
    },
    (e) => {
      toastr.error("Error", "Failed to download");
    }
  );

  const filterRow = (
    <ListFilters
      key="filter-row"
      savedFilters={leadSettings?.filters || []}
      onSaveFilter={onSaveFilter}
      isSavingFilter={savingFilter}
      onRemoveFilter={onRemoveFilter}
      filters={filters}
      onFilter={(searchFilters: Dict<FilterValueType>) => {
        onChangeFilter(searchFilters, search, page, perPage, dateFilters, sort);
        setFilters(searchFilters);
      }}
    >
      <MultiSelectFilter
        label="Lead Intent"
        filterKey="leadIntent"
        key="leadIntent"
        allowSearch
        options={masterData.data?.leadIntents || []}
        valueField="_id"
        labelField="name"
      />
      <MultiSelectFilter
        label="Lead Quality Reasons"
        filterKey="qualityReasons"
        key="qualityReasons"
        allowSearch
        options={masterData.data?.leadQualityReasons || []}
        valueField="code"
        labelField="reason"
      />
      <MultiSelectFilter
        label="Lead Source"
        filterKey="leadSource"
        key="leadSource"
        allowSearch
        options={masterData.data?.leadSources || []}
        valueField="_id"
        labelField="name"
      />
      <MultiSelectFilter
        label="Decision Time Frame"
        filterKey="DecisionTimeFrame"
        key="decisionTimeFrame"
        allowSearch
        options={masterData.data?.decisionTimeFrames || []}
        valueField="code"
        labelField="name"
      />
      <MultiSelectFilter
        label="Lead Initial TouchPoint"
        filterKey="initialTouchPoint"
        key="initialTouchPoint"
        allowSearch
        options={masterData.data?.initialTouchPoints || []}
        valueField="_id"
        labelField="name"
      />
      <MultiSelectFilter
        label="Created By"
        filterKey={"createdByIds"}
        key={"createdByIds"}
        allowSearch
        options={allStaffs || []}
        valueField="_id"
        labelField="name"
      />
      <StaffMultiSelectFilter
        label="Transaction Manager"
        filterKey="salesPersonIds"
        key="salesPersonIds"
        allowSearch
        loadData={() =>
          api.user.getSalesPersons({
            list: "lead-assignees",
            sortBy: "name",
            sortOrder: SortOrder.ASC,
          })
        }
      />
      <StaffMultiSelectFilter
        label="Support Executive"
        filterKey="crmAgentIds"
        key="crmAgentIds"
        allowSearch
        loadData={() =>
          api.user.getCrmAgents({
            sortBy: "name",
            sortOrder: SortOrder.ASC,
          })
        }
      />
      <MultiSelectFilter
        label="Lead Channel"
        filterKey="leadChannels"
        key="leadChannels"
        labelField="label"
        valueField="value"
        options={[
          { label: "Unassigned", value: null },
          { label: "ECOM", value: "ECOM" },
          { label: "Facebook", value: "facebook" },
          { label: "TikTok", value: "tiktok" },
          { label: "Linkedin", value: "linkedin" },
          { label: "WAP", value: "wapp" },
          { label: "ServiceAdvisor", value: "ServiceAdvisor" },
          { label: "BodyShop", value: "BodyShop" },
        ]}
      ></MultiSelectFilter>
      <MultiSelectFilter
        label="Lead Quality"
        filterKey="quality"
        key="quality"
        labelField="name"
        valueField="code"
        options={masterData.data?.leadQualities || []}
      ></MultiSelectFilter>
      <SingleSelectFilter
        label="Lead Stage"
        filterKey="stageCode"
        key="stageCode"
        allowSearch
        options={Object.keys(LeadStage).map((key) => {
          return {
            label: convertPascalCaseToWords(key),
            value: key,
          };
        })}
        valueField="value"
        labelField="label"
      />
      <MultiSelectFilter
        label="Location"
        filterKey="cityIds"
        key="cityIds"
        allowSearch
        options={allCities || []}
        valueField="_id"
        labelField="cityName"
      />
    </ListFilters>
  );

  const actions = [];

  if (canCreateLeads) {
    actions.push({
      text: "Add Lead",
      onClick: () => setLeadModal({ isOpen: true }),
    });
  }

  if (selected.length > 0) {
    actions.push({
      text: "Bulk Action",
      onClick: () => {
        if (selected.length === 0) {
          toastr.info("Info", "Please select leads");
        } else {
          setShowBulkActionPopup(true);
        }
      },
    });
    if (canDeleteLeads) {
      actions.push({
        text: "Delete",
        onClick: () => {
          if (selected.length === 0) {
            toastr.info("Info", "Please select leads");
          } else {
            setShowDeletePopup(true);
          }
        },
      });
    }
  }

  const renderDelete = () => {
    return showDeletePopup ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>Are you sure want to delete this leads</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={() => {
                          setShowDeletePopup(false);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={onRemoveLeads}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const headerActions = [
    <ListHeaderSearch
      placeholder="Customer Name / Mobile/ Email"
      serachTerm={search}
      onSearch={(val: string) => {
        onChangeFilter(filters, val, page, perPage, dateFilters, sort);
        setSearch(val);
      }}
    />,
    <DateRangeListFilter
      default={dateFilters}
      onChange={(d?: IDateRange) => {
        setDateFilters({
          startDate: d?.startDate,
          endDate: d?.endDate,
        });
        if (loaded) {
          onChangeFilter(
            filters,
            search,
            page,
            perPage,
            {
              startDate: d?.startDate,
              endDate: d?.endDate,
            },
            sort
          );
        }
      }}
    />,
  ];

  if (canCreateLeads || canBulkImportLead || selected.length > 0) {
    headerActions.push(<div className="position-relative">
      <ListHeaderDropdown text="Action" actions={actions} /> 
     {showBulkActionPopup && (
            <BulkAction
              leadIds={selected}
              onClose={(shouldReload) => {
                if (shouldReload) {
                  setPage(1);
                  onChangeFilter(
                    filters,
                    search,
                    1,
                    perPage,
                    dateFilters,
                    sort
                  );
                  if (page === 1) {
                    reload(
                      search,
                      filters,
                      1,
                      perPage,
                      sort,
                      dateFilters,
                      showArchived
                    );
                  }
                }
                setSelected([])
                setShowBulkActionPopup(false);
              }}
            ></BulkAction>
          )}
    </div>);
  }

  if (data?.count && canDownloadLeads) {
    headerActions.push(
      downloading ? (
        <span className="cursor-pointer p-0 m-0">
          <Spinner height={40} width={40} />
        </span>
      ) : (
        <span className="cursor-pointer" onClick={downloadLeads}>
          <i className="icon-download font-xxl"></i>
        </span>
      )
    );
  }

  const formatData = (details: any) => {
    return details.map((item: Lead) => ({
      ...item,
      campaignDetails: item.campaignid
        ? item.utm_medium
          ? `${item.utm_medium.toUpperCase()} (${item.campaignid})`
          : `${item.campaignid}`
        : "",
    }));
  };
  return (
    <LeadCalendarProvider>
      <AppLayout>
        <ConfirmationDialogProvider>
          {!leadModal.isOpen && (
            <>
              <ListHeader additionalRows={[filterRow]} actions={headerActions}>
                <div className="d-flex">
                  <h2 className="m-0 d-flex flex-column">
                    <div className="d-flex align-items-center">
                      Leads
                      {canViewArchivedLeads && <div className="d-flex align-items-center ml-3">
                        <div className="switch switch--sm">
                          <input
                            type="checkbox"
                            name=""
                            onChange={(val) => {
                              setShowArchived(!showArchived);
                            }}
                            id="archiveSwitch"
                            checked={showArchived}
                          />
                          <label htmlFor="archiveSwitch">
                            <div className="switch-handle ml-0 mr-2"></div>
                          </label>
                        </div>
                        <span className="font-sm  font-weight-semibold">
                          {showArchived ? "Hide Archived" : "Show Archived"}{" "}
                        </span>
                      </div>}
                    </div>
                    {data && (
                      <span className="font-xs text-muted font-weight-semibold pt-2">
                        {data.count} Total
                        <span className="font-weight-normal pt-2 ml-1">
                          Updated <Moment fromNow date={data.lastUpdatedAt} />
                        </span>
                        {selected.length > 0 && (
                          <span className="font-weight-normal pt-2 ml-1">
                            Selected {selected.length} items
                          </span>
                        )}
                      </span>
                    )}
                  </h2>
                </div>
              </ListHeader>
              {(!loaded || loading) && (
                <ContainerLoader
                  height={window.innerHeight - 300}
                ></ContainerLoader>
              )}
              {loaded && !loading && (
                <ListTable
                  data={formatData(data?.data)}
                  showSelectOption={canBulkAssignLeads || canDeleteLeads}
                  showColumnSettings
                  selectedColumns={leadSettings?.columnSettings?.columns}
                  onColumnSettingsSaved={saveColumnSettings}
                  selectedValues={selected}
                  onSelect={setSelected}
                  onSort={(sortKey: string, sortOrder?: SortOrder) => {
                    setSort({ sortBy: sortKey, sortOrder });
                    onChangeFilter(
                      filters,
                      search,
                      page,
                      perPage,
                      dateFilters,
                      {
                        sortBy: sortKey,
                        sortOrder,
                      }
                    );
                  }}
                  sort={sort}
                  trProps={(d: Lead) => {
                    return {
                      className: classnames({
                        new: false,
                        "cursor-pointer": true,
                      }),
                    };
                  }}
                  onRowClick={(d: Lead) => onLeadClick(d._id)}
                >
                  {columns}
                </ListTable>
              )}
              <Pagination
                currentPage={page}
                totalPages={data?.totalPages}
                onPageChange={(pg: number) => {
                  setPage(pg);
                  onChangeFilter(
                    filters,
                    search,
                    pg,
                    perPage,
                    dateFilters,
                    sort
                  );
                }}
                perPage={perPage}
                onPerPageChange={(pg: number) => {
                  setPerPage(pg);
                  onChangeFilter(filters, search, page, pg, dateFilters, sort);
                }}
                count={data?.count}
                label="leads"
              />
            </>
          )}
          {leadModal.isOpen && (
            <AddLeadForm
              onClose={() => setLeadModal({})}
              id={leadModal.leadId}
              leadCreated={leadCreated}
            />
          )}
        
          {/* {showBulkActionPopup && (
            <BulkAction
              leadIds={selected}
              onClose={(shouldReload) => {
                if (shouldReload) {
                  setPage(1);
                  onChangeFilter(
                    filters,
                    search,
                    1,
                    perPage,
                    dateFilters,
                    sort
                  );
                  if (page === 1) {
                    reload(
                      search,
                      filters,
                      1,
                      perPage,
                      sort,
                      dateFilters,
                      showArchived
                    );
                  }
                }
                setSelected([])
                setShowBulkActionPopup(false);
              }}
            ></BulkAction>
          )} */}
          {renderDelete()}
        </ConfirmationDialogProvider>
      </AppLayout>
    </LeadCalendarProvider>
  );
};

export default Home;
